import { defineStore } from "pinia";
import type { Product } from "~/stores/products";
import { normaliseColour, colourToHex, FALLBACK_COLOUR } from "~/utils/colour";
import { DELIVERY_BUCKETS, bucketDeliveryTime } from "~/utils/deliveryTime";
import { parseSpecifications } from "~/utils/specifications";

export const sortOptions = [
  { label: "Relevanz", value: "relevance" },
  { label: "Preis aufsteigend", value: "price_asc" },
  { label: "Preis absteigend", value: "price_desc" },
  { label: "Beliebtheit", value: "popularity" },
  { label: "Neueste", value: "newest" },
];

export const useFiltersStore = defineStore("filters", () => {
  const activeSubcategory = ref<string | null>(null);
  const sortBy = ref(
    typeof window !== 'undefined'
      ? localStorage.getItem("moebix:sortBy") || "relevance"
      : "relevance",
  );
  const viewMode = ref(
    typeof window !== 'undefined'
      ? (localStorage.getItem("moebix:viewMode") as "grid" | "list") || "grid"
      : "grid",
  );

  if (typeof window !== 'undefined') {
    watch(viewMode, (val) => localStorage.setItem("moebix:viewMode", val));
    watch(sortBy, (val) => localStorage.setItem("moebix:sortBy", val));
  }
  const priceRange = ref<[number, number]>([0, 5000]);
  const priceMin = ref(0);
  const priceMax = ref(5000);
  const selectedMerchants = ref<string[]>([]);
  const selectedBrands = ref<string[]>([]);
  const selectedColours = ref<string[]>([]);
  const selectedDeliveryBuckets = ref<string[]>([]);
  const selectedSpecifications = ref<Record<string, string[]>>({});
  const currentPage = ref(1);
  const perPage = 40;

  const products = ref<Product[]>([]);

  const setProducts = (items: Product[]) => {
    const isAppend = items.length > products.value.length && products.value.length > 0;
    products.value = items;
    if (items.length > 0) {
      const prices = items.map((p) => p.price).filter((p) => p > 0);
      const min = Math.floor(Math.min(...prices));
      const max = Math.ceil(Math.max(...prices));
      priceMin.value = min;
      priceMax.value = max;
      if (isAppend) {
        // Expand user's selected range if new products fall outside it
        priceRange.value = [
          Math.min(priceRange.value[0], min),
          Math.max(priceRange.value[1], max),
        ];
      } else {
        priceRange.value = [min, max];
      }
    }
  };

  const availableMerchants = computed(() => {
    return [
      ...new Set(products.value.map((p) => p.merchantName).filter(Boolean)),
    ].sort() as string[];
  });

  function isValidBrand(b: string | null | undefined): b is string {
    if (!b) return false;
    const t = b.trim();
    return t.length > 1 && t !== "-" && t !== "–" && t !== "n/a" && t !== "N/A";
  }

  const hasBrandless = computed(() =>
    products.value.some((p) => !isValidBrand(p.brand)),
  );

  const availableBrands = computed(() => {
    const brands = [
      ...new Set(
        products.value.map((p) => p.brand?.trim()).filter(isValidBrand),
      ),
    ].sort();
    if (hasBrandless.value) brands.push("Sonstige");
    return brands;
  });

  // Verfügbare Farben aus allen Quellen — dedupliziert nach Hex-Code
  // Pro eindeutigem Hex-Wert wird nur eine Farbe angezeigt
  // actualColour (aus Awin custom_2) wird bevorzugt, da sie die echte
  // Farbbezeichnung enthält (z.B. "schwarz" statt "KRONOS 07")
  const availableColours = computed((): string[] => {
    const seenHex = new Map<string, string>(); // hex → erster Farbname
    for (const p of products.value) {
      const meta = (p as any).metadata || {};
      for (const raw of [
        meta.actualColour,
        p.variantColour,
        meta.colour,
        meta.fashionColour,
        meta.variantColour,
      ]) {
        const n = normaliseColour(raw);
        if (!n) continue;
        const hex = colourToHex(n);
        // Farben die auf FALLBACK fallen weglassen — unbekannte Farbe sieht gleich aus
        if (hex === FALLBACK_COLOUR) continue;
        if (!seenHex.has(hex)) seenHex.set(hex, n);
      }
    }
    return [...seenHex.values()].sort();
  });

  // Verfügbare Lieferzeit-Buckets (in kanonischer Reihenfolge)
  const availableDeliveryBuckets = computed((): string[] => {
    const seen = new Set<string>();
    for (const p of products.value) {
      const bucket = bucketDeliveryTime((p as any).metadata?.deliveryTime);
      if (bucket) seen.add(bucket);
    }
    const order = DELIVERY_BUCKETS.map((b) => b.label);
    return [...seen].sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  });

  // Dynamische Spec-Filter: nur Labels mit >= 2 distinct Werten, max 6
  const MAX_SPEC_FILTERS = 6;
  const availableSpecifications = computed((): Record<string, string[]> => {
    const acc: Record<string, Set<string>> = {};
    for (const p of products.value) {
      const specs = parseSpecifications((p as any).metadata?.specifications);
      for (const [label, vals] of Object.entries(specs)) {
        if (!acc[label]) acc[label] = new Set();
        for (const v of vals) acc[label].add(v);
      }
    }
    const result: Record<string, string[]> = {};
    const entries = Object.entries(acc)
      .filter(([, s]) => s.size >= 2)
      .sort((a, b) => b[1].size - a[1].size)
      .slice(0, MAX_SPEC_FILTERS);
    for (const [label, valSet] of entries) {
      result[label] = [...valSet].sort();
    }
    return result;
  });

  const filteredProducts = computed(() => {
    let items = [...products.value];

    if (activeSubcategory.value) {
      items = items.filter((p) => p.categoryId === activeSubcategory.value);
    }

    items = items.filter(
      (p) => p.price >= priceRange.value[0] && p.price <= priceRange.value[1],
    );

    if (selectedMerchants.value.length > 0) {
      items = items.filter((p) =>
        selectedMerchants.value.includes(p.merchantName || ""),
      );
    }

    if (selectedBrands.value.length > 0) {
      const withSonstige = selectedBrands.value.includes("Sonstige");
      const realBrands = selectedBrands.value.filter((b) => b !== "Sonstige");
      items = items.filter((p) => {
        if (withSonstige && !isValidBrand(p.brand)) return true;
        return isValidBrand(p.brand) ? realBrands.includes(p.brand) : false;
      });
    }

    // Farbe — actualColour (echte Farbe aus custom_2) bevorzugt
    if (selectedColours.value.length > 0) {
      items = items.filter((p) => {
        const meta = (p as any).metadata || {};
        const colours = [
          meta.actualColour,
          p.variantColour,
          meta.colour,
          meta.fashionColour,
          meta.variantColour,
        ]
          .map(normaliseColour)
          .filter(Boolean) as string[];
        return colours.some((c) => selectedColours.value.includes(c));
      });
    }

    // Lieferzeit
    if (selectedDeliveryBuckets.value.length > 0) {
      items = items.filter((p) => {
        const bucket = bucketDeliveryTime((p as any).metadata?.deliveryTime);
        return (
          bucket !== null && selectedDeliveryBuckets.value.includes(bucket)
        );
      });
    }

    // Spezifikationen (AND über Labels, OR innerhalb eines Labels)
    const activeSpecs = Object.entries(selectedSpecifications.value).filter(
      ([, vals]) => vals.length > 0,
    );
    if (activeSpecs.length > 0) {
      items = items.filter((p) => {
        const specs = parseSpecifications((p as any).metadata?.specifications);
        return activeSpecs.every(([label, selectedVals]) =>
          (specs[label] || []).some((v) => selectedVals.includes(v)),
        );
      });
    }

    switch (sortBy.value) {
      case "price_asc":
        items.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        items.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        items.reverse();
        break;
    }

    return items;
  });

  const totalPages = computed(() =>
    Math.ceil(filteredProducts.value.length / perPage),
  );

  const paginatedProducts = computed(() => {
    return filteredProducts.value.slice(0, currentPage.value * perPage);
  });

  const activeFilterCount = computed(() => {
    let count = 0;
    if (
      priceRange.value[0] > priceMin.value ||
      priceRange.value[1] < priceMax.value
    )
      count++;
    count += selectedMerchants.value.length;
    count += selectedBrands.value.length;
    count += selectedColours.value.length;
    count += selectedDeliveryBuckets.value.length;
    count += Object.values(selectedSpecifications.value).flat().length;
    return count;
  });

  const resetFilters = () => {
    activeSubcategory.value = null;
    priceRange.value = [priceMin.value, priceMax.value];
    selectedMerchants.value = [];
    selectedBrands.value = [];
    selectedColours.value = [];
    selectedDeliveryBuckets.value = [];
    selectedSpecifications.value = {};
    currentPage.value = 1;
  };

  return {
    activeSubcategory,
    sortBy,
    viewMode,
    priceRange,
    priceMin,
    priceMax,
    selectedMerchants,
    selectedBrands,
    selectedColours,
    selectedDeliveryBuckets,
    selectedSpecifications,
    currentPage,
    perPage,
    products,
    setProducts,
    availableMerchants,
    availableBrands,
    availableColours,
    availableDeliveryBuckets,
    availableSpecifications,
    filteredProducts,
    paginatedProducts,
    totalPages,
    activeFilterCount,
    resetFilters,
    sortOptions,
  };
});
